import { useStoreState } from '@ariakit/react'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { ALERT_INTENT, Alert } from '@syconium/little-miss-figgy'

import { PREFERENCE_SUPPORTED_LOCATIONS } from '../../../../../../app/_components/pages/myfigs/preferences/constants'
import { useUpdateEmbroideryPreferences } from '../../../../../../app/_hooks/useEmbroideryPreferences'
import { useAuthentication } from '../../../../../../app/_providers/AuthenticationProvider.client'
import { useCart } from '../../../../../../app/_providers/CartProvider.client'
import { useTranslation } from '../../../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { useEmbroideryV2Flag } from '../../../../../../containers/EmbroideryContainer/useEmbroideryV2Flag'
import { isEmbroiderableProduct } from '../../../../../../lib/utils'
import { PreferencesToggle } from '../../../../Modal/primitives/shared-form-components'
import { ThreadEmojiWrapper } from '../../../../Modal/primitives/shared-form-styles'
import { FormBody } from '../../../../Modal/primitives/shared-form-styles'
import { SubmissionButtons } from '../../../../Modal/primitives/SubmissionButtons/SubmissionButtons'
import { useValidPlacementOptionsForLegacyFlow } from '../../../../unorganized'
import { buildLogoPreferenceQuery } from '../../utils'
import * as Styles from '../EmbroideryLogoSetup/styles'
import { LogoFormContainer } from '../LogoFormContainer'

import { LogoPlacement } from './LogoPlacement'
import { LogoSelector } from './LogoSelector'

export const AddEmbroideryLogo: FC = () => {
  const LogoPreferencesToggleWrapper = styled.div`
    margin-bottom: ${o => o.theme.spacing(6)};
  `
  const {
    addLogoForm: form,
    addLogoIsDirty: isDirty,
    resetLogoForms,
    logoDisplayPrices,
    includesDigitization,
    transientLogo,
  } = LogoFormContainer.useContainer()

  const { cart } = useCart()
  const { productHandle, savedEmbroideryDefaultFormValues, productEmbroideryConfig, withPricing } =
    useEmbroidery()
  const { isLoggedIn } = useAuthentication()
  /**
   * Temp blocking of MyFigs Preferences for the embroidery multi-placement
   * this should be cleaned up as a part of the Preferences/Multiplacement integration LFE-125
   * */
  const preferencesEnabled = !useEmbroideryV2Flag({ skip: false })
  const { updateLogoEmbroideryPreferences } = useUpdateEmbroideryPreferences()

  const formValues = useStoreState(form, s => s.values)
  const formErrors = useStoreState(form, s => s.errors)

  const selectedLogoId = formValues.logoFile?.id

  const LOGO = savedEmbroideryDefaultFormValues?.LOGO_DTC

  /** For "save preferences" toggle. Once the form is dirtied
   * we uncheck, but don't recheck if form becomes 'undirty'
   */
  const hasFormDirtied = useRef<boolean>(false)

  // Don't show save preferences toggle if the placements a shopper can pick include locations unsupported by MyFigs
  const logoPlacements = useValidPlacementOptionsForLegacyFlow(
    productEmbroideryConfig?.options.logo?.placements
  )
  const supportsPreferenceLocation = Boolean(
    logoPlacements?.length &&
      logoPlacements.every(p => PREFERENCE_SUPPORTED_LOCATIONS.has(p.location))
  )

  const isSelectedLogoApproved = useMemo<boolean>(() => {
    return formValues.logoFile?.status === 'APPROVED'
  }, [formValues.logoFile?.status])

  const hasInitialPreferences = useMemo<boolean>(() => {
    const formId = formValues.logoFileId
    if (!formId) return false
    return formId === LOGO?.logoFileId
  }, [LOGO, formValues])

  const [savePreferencesChecked, setSavePreferencesChecked] =
    useState<boolean>(hasInitialPreferences)

  const onToggle = () => {
    setSavePreferencesChecked(!savePreferencesChecked)
  }

  const multipleUniqueDigitizationsBeingRequested =
    includesDigitization &&
    Boolean(selectedLogoId) &&
    Object.values(cart.items).some(
      item =>
        isEmbroiderableProduct(item) &&
        item.properties._logo_id === selectedLogoId &&
        item.productGroupHandle !== productHandle
    )

  const {
    embroidery: {
      modal: {
        forms: {
          preferences: {
            logoPreferenceToggleLabel,
            logoPreferenceTooltip,
            logoPreferenceTooltipAriaLabel,
          },
          dtcLogoEmbroideryForm: { digitizationDisclaimer, digitizationOneAtATime },
        },
      },
    },
  } = useTranslation()

  const onSubmit = async (e: React.FormEvent) => {
    // stopPropagation prevents outer forms from also submitting
    e.preventDefault()
    e.stopPropagation()

    const hasErrors = Object.values(formErrors).some(Boolean)
    if (savePreferencesChecked && formValues.logoFileId && !hasErrors && isDirty) {
      const query = buildLogoPreferenceQuery(formValues)
      await updateLogoEmbroideryPreferences(query)
    }

    form.submit()
  }

  useEffect(() => {
    if (LOGO?.logoFileId && !hasFormDirtied.current && isDirty) {
      hasFormDirtied.current = true
      setSavePreferencesChecked(false)
    }
  }, [isDirty, LOGO?.logoFileId, setSavePreferencesChecked])

  return (
    <>
      <FormBody onSubmit={onSubmit}>
        <LogoSelector />

        <LogoPlacement />
        <Styles.StyledHr />
        {includesDigitization && (
          <WarningLabel>
            <ThreadEmojiWrapper aria-label='thread' role='img'>
              🧵
            </ThreadEmojiWrapper>
            <div>{digitizationDisclaimer}</div>
          </WarningLabel>
        )}
        {preferencesEnabled &&
          isLoggedIn &&
          isSelectedLogoApproved &&
          supportsPreferenceLocation && (
            <LogoPreferencesToggleWrapper>
              <PreferencesToggle
                checked={savePreferencesChecked}
                onToggle={onToggle}
                switchLabel={logoPreferenceToggleLabel}
                tooltipText={logoPreferenceTooltip}
                tooltipAriaLabel={logoPreferenceTooltipAriaLabel}
              />
            </LogoPreferencesToggleWrapper>
          )}
        <SubmissionButtons
          displayPrice={logoDisplayPrices.total}
          withPricing={withPricing}
          disableReset={!includesDigitization || Boolean(transientLogo)}
          onReset={() => {
            resetLogoForms()
          }}
        />
        {multipleUniqueDigitizationsBeingRequested && (
          <WarnAlert intent={ALERT_INTENT.Warn}>
            <Alert.Message>{digitizationOneAtATime}</Alert.Message>
          </WarnAlert>
        )}
      </FormBody>
    </>
  )
}

const WarningLabel = styled(Styles.WarningLabel)`
  margin: ${({ theme }) => theme.spacing(6, 0, 6, 0)};
  gap: ${({ theme }) => theme.spacing(4)};
`

const WarnAlert = styled(Alert)`
  margin: ${({ theme }) => theme.spacing(6, 0, 0, 0)};
`
